/**
 * Camera scanner, as a modal or embedded in a page.
 *
 * Every code the camera reads goes to HeadlightQR.receiveScan, so the active scan mode decides
 * what happens; the camera itself has no opinion about entities.
 */

const libPictView = require('pict-view');
const CameraScanSource = require('../scanning/Camera-Scan-Source.js');
const libMatrix = require('../rendering/QR-Matrix.js');

const MODAL_ID = 'HeadlightQR-CameraModal';

const _DefaultConfiguration =
{
	ViewIdentifier: 'HeadlightQR-Camera',
	AutoRender: false,
	CSS: /*css*/`
.hlqr-camera-modal { position: fixed; inset: 0; z-index: 1200; display: flex; align-items: center; justify-content: center; background: rgba(15, 23, 42, 0.7); }
.hlqr-camera-card { width: min(92vw, 480px); background: var(--theme-color-background-panel, #fff); color: var(--theme-color-text-primary, #1f2933); border-radius: 10px; overflow: hidden; box-shadow: 0 10px 40px rgba(0,0,0,0.4); }
.hlqr-camera-head { display: flex; align-items: center; justify-content: space-between; padding: 0.75rem 1rem; font-weight: 600; }
.hlqr-camera-reader { width: 100%; min-height: 240px; background: #000; }
.hlqr-camera-foot { padding: 0.6rem 1rem; font-size: 0.85rem; min-height: 2.2rem; }
.hlqr-camera-button { border: 1px solid var(--theme-color-border-default, #d5dbe1); background: transparent; color: inherit; border-radius: 6px; padding: 0.3rem 0.8rem; cursor: pointer; }
`,
};

class HeadlightQRCamera extends libPictView
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, Object.assign({}, _DefaultConfiguration, pOptions), pServiceHash);
		/** @type {any} */
		this.pict;
		this.source = null;
		this.onCancel = null;
		this.embeddedID = null;
	}

	/**
	 * Open the camera in a modal.
	 *
	 * @param {Object} [pOptions] - `{ Title, onCancel }`
	 * @return {Promise<void>}
	 */
	async open(pOptions)
	{
		const tmpOptions = pOptions || {};
		await this.close();
		this.onCancel = tmpOptions.onCancel || null;
		const tmpModal = document.createElement('div');
		tmpModal.id = MODAL_ID;
		tmpModal.className = 'hlqr-camera-modal';
		tmpModal.setAttribute('role', 'dialog');
		tmpModal.setAttribute('aria-modal', 'true');
		tmpModal.innerHTML = `<div class="hlqr-camera-card">`
			+ `<div class="hlqr-camera-head"><span>${libMatrix.escapeXML(tmpOptions.Title || 'Scan a code')}</span><button type="button" class="hlqr-camera-button" data-hlqr-camera-cancel>Cancel</button></div>`
			+ `<div id="${MODAL_ID}-Reader" class="hlqr-camera-reader"></div>`
			+ `<div class="hlqr-camera-foot" id="${MODAL_ID}-Status">Point the camera at the code.</div></div>`;
		document.body.appendChild(tmpModal);
		tmpModal.querySelector('[data-hlqr-camera-cancel]').addEventListener('click', () => this.cancel());
		await this.startSource(`${MODAL_ID}-Reader`, `${MODAL_ID}-Status`);
	}

	/**
	 * Run the camera inside an element already on the page (continuous session scanning).
	 *
	 * @param {string} pElementID
	 * @return {Promise<void>}
	 */
	async openEmbedded(pElementID)
	{
		await this.close();
		this.embeddedID = pElementID;
		await this.startSource(pElementID, null);
	}

	/**
	 * @param {string} pReaderID
	 * @param {string|null} pStatusID
	 */
	async startSource(pReaderID, pStatusID)
	{
		const tmpProvider = this.pict.providers.HeadlightQR;
		if (!CameraScanSource.isSupported())
		{
			this.setStatus(pStatusID, 'This browser can\'t open a camera here. Camera scanning needs HTTPS (or localhost).');
			return;
		}
		this.source = new CameraScanSource(
			{
				ElementID: pReaderID,
				onScan: (pText) => tmpProvider.receiveScan(pText, { Source: 'camera' }),
				onError: (pError) => this.setStatus(pStatusID, `The camera couldn't start: ${(pError && pError.message) || pError}`),
			});
		try
		{
			await this.source.start();
		}
		catch (pError)
		{
			this.source = null;
		}
	}

	/**
	 * @param {string|null} pStatusID
	 * @param {string} pMessage
	 */
	setStatus(pStatusID, pMessage)
	{
		if (!pStatusID)
		{
			return;
		}
		const tmpElement = document.getElementById(pStatusID);
		if (tmpElement)
		{
			tmpElement.textContent = pMessage;
		}
	}

	cancel()
	{
		const tmpOnCancel = this.onCancel;
		this.onCancel = null;
		this.close();
		if (tmpOnCancel)
		{
			tmpOnCancel();
		}
	}

	/**
	 * @return {Promise<void>}
	 */
	async close()
	{
		if (this.source)
		{
			const tmpSource = this.source;
			this.source = null;
			await tmpSource.stop();
		}
		const tmpModal = (typeof document !== 'undefined') ? document.getElementById(MODAL_ID) : null;
		if (tmpModal)
		{
			tmpModal.remove();
		}
		this.embeddedID = null;
	}

	/** @return {boolean} */
	isOpen()
	{
		return !!this.source;
	}
}

module.exports = HeadlightQRCamera;
module.exports.default_configuration = _DefaultConfiguration;
